import { Box, Button, Stack, Typography } from '@mui/material';
import { avsStrategyLabels, avsTypeLabels, rollupTypeLabels } from 'pages/avs/helpers/labels';
import { Checked } from 'pages/raas/NewDeployment/SummaryPanel';
import { useEffect, useMemo } from 'react';
import { FieldName, useFormContext, useFormState } from 'react-hook-form';
import { AVS_QUORUMS, AVS_TYPES } from 'shared/types/avs';
import { RollupType } from 'shared/types/protoc-gen/rollup';
import { truncate } from 'shared/utils/strings';

import { AVSFormValues } from '.';

const labelMap: Record<FieldName<AVSFormValues>, string> = {
  // Order of the keys of this map is also the order of appearance on the summary panel.
  avsName: 'AVS Name:',
  avsType: 'AVS Type:',
  chains: 'Chains:',
  compiledOutput: 'Compiled: ',
  initArgs: 'initialize() args: ',
  constructorArgs: 'constructor() args: ',
  aggregatorHandlerName: 'Aggregator Handler: ',
  strategies: 'Strategies:',
  ecdsaWeight: 'Threshold Weight: ',
};

function getDisplayValue(fieldName: FieldName<AVSFormValues>, value: any) {
  switch (fieldName) {
    case 'avsType':
      return avsTypeLabels?.[value as AVS_TYPES];
    case 'strategies':
      return value?.map((cur: AVS_QUORUMS) => avsStrategyLabels?.[cur])?.join(', ');
    case 'chains':
      return value
        ?.map((cur: { chainId: { value: number }; rollupType: { value: RollupType } }) =>
          cur?.chainId?.value && cur?.rollupType?.value
            ? `${cur?.chainId?.value}|${rollupTypeLabels?.[cur?.rollupType?.value]}`
            : '',
        )
        ?.join(', ');
    case 'compiledOutput':
      return String(!!value?.bytecode && value?.bytecode !== '0xundefined');
    case 'initArgs':
      return value
        ?.filter((cur: any) => !!cur?.value)
        ?.map((cur: any) =>
          cur?.type === 'address' || cur?.type?.startsWith?.('bytes')
            ? truncate(cur?.value || '', '…', 4, 2)
            : cur?.value,
        )
        ?.join(', ');
    case 'constructorArgs':
      return value
        ?.filter((cur: any) => !!cur?.value)
        ?.map((cur: any) =>
          cur?.type === 'address' || cur?.type?.startsWith?.('bytes')
            ? truncate(cur?.value || '', '…', 4, 2)
            : cur?.value,
        )
        ?.join(', ');
    case 'aggregatorHandlerName':
      return value?.value;
    default:
      return value && String(value);
  }
}

export default function AVSSummaryPanel() {
  const form = useFormContext<AVSFormValues>();
  const formValues = form?.watch();
  const formState = useFormState<AVSFormValues>({ control: form.control });

  useEffect(() => {
    console.debug('formState: ', formState);
  }, [formState]);

  const summaryItems = useMemo(
    () =>
      Object.entries(formValues)
        ?.map(([name, value]) => {
          const fieldName = name as keyof AVSFormValues;

          let isError = false;
          const fieldErrors = (formState?.errors as any)?.[name];

          if (fieldErrors?.message || fieldErrors?.root?.messge) {
            isError = true;
          } else if (Array.isArray(fieldErrors)) {
            isError = fieldErrors?.some?.((cur: any) =>
              Object.values(cur || {})?.some?.(val => Boolean(val)),
            );
          } else if (Object.values(fieldErrors || {})?.length) {
            isError = true;
          }

          return {
            isError,
            fieldName,
            displayValue: getDisplayValue(fieldName, value),
            value,
            touched: Boolean(
              (formState?.touchedFields as Record<string, boolean | undefined>)?.[fieldName],
            ),
            valid: !form.getFieldState(fieldName as any)?.invalid,
            dirty: form.getFieldState(fieldName as any)?.isDirty,
          };
        })
        ?.sort(
          (a, b) =>
            Object.keys(labelMap || {})?.findIndex(cur => cur === a.fieldName) -
            Object.keys(labelMap || {})?.findIndex(cur => cur === b.fieldName),
        ),
    [formValues, formState?.errors, formState?.touchedFields, form],
  );

  const scrollToStep = (fieldName: FieldName<AVSFormValues>) => () => {
    const el = document.getElementById(`step_${fieldName}`);

    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.08)',
        background: 'linear-gradient(118.58deg, #F6F6F6 0%, #E7E7E7 100%)',
        m: { md: 5 },
        position: 'sticky',
        top: 40,
      }}
    >
      <Stack alignItems="center" direction="row" gap={1} sx={{ px: 5, py: 2 }}>
        <Typography pt={5} variant="captionC">
          Summary
        </Typography>
      </Stack>
      {summaryItems?.map(cur => (
        <Stack
          alignItems="center"
          direction="row"
          gap={1}
          key={cur.fieldName}
          onClick={scrollToStep(cur.fieldName)}
          sx={{ px: 5, py: 2, borderTop: '1px solid rgba(32, 39, 35, 0.08)', cursor: 'pointer' }}
        >
          <Checked
            valid={
              cur.valid && (cur.touched || formState?.isSubmitted)
                ? true
                : cur.isError
                ? false
                : undefined
            }
          />
          <Typography variant="caption">{labelMap?.[cur?.fieldName]}</Typography>
          <Typography sx={{ wordBreak: 'break-all' }} variant="captionM">
            {cur.displayValue}
          </Typography>
        </Stack>
      ))}
      <Stack
        alignItems="center"
        direction="row"
        gap={1}
        sx={{ px: 5, py: 2, borderTop: '1px solid rgba(32, 39, 35, 0.08)' }}
      >
        <Button fullWidth type="submit">
          Submit
        </Button>
      </Stack>
    </Box>
  );
}
